import React, { ReactNode, FC, useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import {
  getAuthInfo,
  isUserAuthenticated,
  redirectToLogin,
  setAuthTokens,
} from '../../utils/auth';
import { AUTH_REFRESH_TIME_WINDOW_MILLISECONDS } from '../../constants';
import httpService from '../../services/http';
import { ExacteraPostMessage } from '../../models/exacteraPostMessage';
import { getAppConfig } from '../../utils/configuration';

interface AuthenticationProps {
  children: ReactNode;
}

export const Authentication: FC<AuthenticationProps> = ({ children }): React.ReactElement | null => {
  const [shouldRenderPage, setShouldRenderPage] = useState(isUserAuthenticated());
  const [isInitializing, setIsInitializing] = useState(true);
  const idleTimeoutMs = 60 * 60 * 1000;

  // Allow up to 100ms for auth initialization which may be coming from a post message
  setTimeout(() => {
    setIsInitializing(false);
  }, 100);

  const refreshToken = async (): Promise<void> => {
    const newAuthTokens = await httpService.refreshToken();
    setAuthTokens(newAuthTokens);
  };

  useEffect(() => {
    const appConfig = getAppConfig();
    const intervalId = setInterval(() => {
      if (!isUserAuthenticated()) {
        console.warn('Word Add-in: User is not authenticated, redirecting to login');
        setShouldRenderPage(false);
        return;
      }

      const authState = getAuthInfo();
      const nextRefreshTime = authState.expiration - AUTH_REFRESH_TIME_WINDOW_MILLISECONDS;
      if (Date.now() > nextRefreshTime) {
        void refreshToken();
      }
    }, 60 * 1000);

    console.log('Word Add-in: Office context', Office.context);

    const messageHandler = (event: MessageEvent): void => {
      console.log('Word Add-in: Received message:', event);

      if (event.origin.endsWith('.exactera.com') && event.data) {
        const message = JSON.parse(event.data) as ExacteraPostMessage;

        if (!message || !message.type || !message.env || !message.payload) {
          console.warn('Word Add-in: Invalid Exactera message format:', message);
          return;
        }

        if (message.env !== appConfig.xbsEnvironmentShort) {
          console.warn('Word Add-in: Message environment does not match app config:', message.env, appConfig.xbsEnvironmentShort);
          return;
        }

        if (message.type === 'auth') {
          console.log('Word Add-in: Auth message received:', message.payload);
          setAuthTokens(message.payload);
          setShouldRenderPage(isUserAuthenticated());
        } else if (message.type === 'logout') {
          console.log('Word Add-in: Logout message received');
          setShouldRenderPage(false);
        }

        setIsInitializing(false);
      }
    };

    window.addEventListener("message", messageHandler, false);

    // Emit a post message to the top window
    if (window.top) {
      const message: ExacteraPostMessage = {
        env: appConfig.xbsEnvironmentShort,
        type: 'init',
        payload: {
          app: 'exact-report-add-in'
        },
      };
      console.log('Word Add-in: Posting message to top window:', message);
      window.top.postMessage(JSON.stringify(message), '*');
    }

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("message", messageHandler);
    };
  }, []);

  const onIdle = (): void => {
    redirectToLogin();
    return null;
  };

  useIdleTimer({ onIdle, timeout: idleTimeoutMs });

  // Do not render the page until we have initialized either by receiving a post message or reaching a timeout
  // If we are already authenticated, we can render the page immediately
  if (isInitializing && !shouldRenderPage) {
    return null;
  }

  if (shouldRenderPage) {
    return <>{children}</>;
  } else {
    void redirectToLogin();
    return null;
  }
};
